import { AdjustmentsIcon } from "@heroicons/react/solid";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useQuery } from "@tanstack/react-query";
import { Provider } from "jotai";
import dynamic from "next/dynamic";
import React, { ReactNode, useEffect, useState, useMemo } from "react";
import { IconType } from "react-icons";
import { AiFillHeart } from "react-icons/ai";
import { BsLink, BsToggles } from "react-icons/bs";
import { FaArchive, FaBell, FaPlug, FaTasks } from "react-icons/fa";
import { HiUser } from "react-icons/hi";
import { ImLifebuoy } from "react-icons/im";
import {
  MdOutlineIntegrationInstructions,
  MdOutlineSupportAgent
} from "react-icons/md";
import { RiShieldUserFill } from "react-icons/ri";
import { VscKey } from "react-icons/vsc";
import { VscJson } from "react-icons/vsc";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation
} from "react-router-dom";
import { Canary, Icon } from "./components";
import AuthProviderWrapper from "./components/Authentication/AuthProviderWrapper";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { HomepageRedirect } from "./components/HomepageRedirect";
import { NoPermissionsOnboardingPage } from "./components/NoPermissionsOnboardingPage";
import {
  DASHBOARD_VIEW_PROPERTY,
  FALLBACK_VIEW_NAME,
  UUID_REGEX
} from "./components/dashboardViewConstants";
import { withAuthorizationAccessCheck } from "./components/Permissions/AuthorizationAccessCheck";
import { SchemaResource } from "./components/SchemaResourcePage/SchemaResource";
import {
  SchemaResourceType,
  schemaResourceTypes
} from "./components/SchemaResourcePage/resourceTypes";
import { ConfigPageContextProvider } from "./context/ConfigPageContext";
import { useFeatureFlagsContext } from "./context/FeatureFlagsContext";
import { HealthPageContextProvider } from "./context/HealthPageContext";
import { IncidentPageContextProvider } from "./context/IncidentPageContext";
import {
  UserAccessStateContextProvider,
  useUserAccessStateContext
} from "./context/UserAccessContext/UserAccessContext";
import { tables } from "./context/UserAccessContext/permissions";
import { useUser } from "./context";
import {
  canOpenPermissionsTab,
  PERMISSIONS_TAB_RESOURCES
} from "./components/Permissions/PermissionsTabsLinks";
import { MCP_TAB_RESOURCES } from "./components/MCP/McpTabsLinks";

import { GettingStartedPage } from "./pages/GettingStartedPage";
import { ArtifactsPage } from "./pages/Settings/ArtifactsPage";
import { PermissionsSubjectsPage } from "./pages/Settings/PermissionsSubjectsPage";
import McpOverviewPage from "./pages/Settings/mcp/McpOverviewPage";
import McpPlaybooksPage from "./pages/Settings/mcp/McpPlaybooksPage";
import McpViewsPage from "./pages/Settings/mcp/McpViewsPage";
import McpSubjectAccessPage from "./pages/Settings/mcp/McpSubjectAccessPage";
import McpCheckAccessPage from "./pages/Settings/mcp/McpCheckAccessPage";
import ScopesPage from "./pages/Settings/ScopesPage";
import RolesPage from "./pages/Settings/RolesPage";
import RoleBindingsPage from "./pages/Settings/RoleBindingsPage";
import PermissionGroupsPage from "./pages/Settings/PermissionGroupsPage";
import { PermissionsPage } from "./pages/Settings/PermissionsPage";
import { features } from "./services/permissions/features";
import { getViewsForSidebar, ViewSummary } from "./api/services/views";
import { Head } from "./ui/Head";
import { LogsIcon } from "./ui/Icons/LogsIcon";
import { SidebarLayout } from "./ui/Layout/SidebarLayout";
import FullPageSkeletonLoader from "./ui/SkeletonLoader/FullPageSkeletonLoader";
import { ToasterWithCloseButton } from "./ui/ToasterWithCloseButton";
import { stringSortHelper } from "./utils/common";

const TopologyPage = dynamic(
  import("@flanksource-ui/pages/TopologyPage").then((mod) => mod.TopologyPage)
);

const TopologyCardPage = dynamic(
  import("@flanksource-ui/pages/TopologyCard").then(
    (mod) => mod.TopologyCardPage
  )
);

const IncidentDetailsPage = dynamic(
  import("@flanksource-ui/pages/incident/IncidentDetails").then(
    (mod) => mod.IncidentDetailsPage
  )
);

const IncidentListPage = dynamic(
  import("@flanksource-ui/pages/incident/IncidentListPage").then(
    (mod) => mod.IncidentListPage
  )
);

const ConfigListPage = dynamic(
  import("@flanksource-ui/pages/config/ConfigList").then(
    (mod) => mod.ConfigListPage
  )
);

const ConfigDetailsPage = dynamic(
  import("@flanksource-ui/pages/config/details/ConfigDetailsPage").then(
    (mod) => mod.ConfigDetailsPage
  )
);

const ConfigDetailsIndexRedirect = dynamic(
  import(
    "@flanksource-ui/pages/config/details/ConfigDetailsIndexRedirect"
  ).then((mod) => mod.ConfigDetailsIndexRedirect)
);

const ConfigDetailsChangesPage = dynamic(
  import("@flanksource-ui/pages/config/details/ConfigDetailsChangesPage").then(
    (mod) => mod.ConfigDetailsChangesPage
  )
);

const PlaybooksListPage = dynamic(
  import("@flanksource-ui/pages/playbooks/PlaybooksList").then(
    (mod) => mod.PlaybooksListPage
  )
);

const LogsPage = dynamic(
  import("@flanksource-ui/pages/LogsPage").then((mod) => mod.LogsPage)
);

const ApplicationsPage = dynamic(
  import("@flanksource-ui/pages/applications/ApplicationsPage").then(
    (mod) => mod.ApplicationsPage
  )
);

const AuditReportPage = dynamic(
  import("@flanksource-ui/pages/audit-report/AuditReportPage").then(
    (mod) => mod.AuditReportPage
  )
);

const ViewPage = dynamic(
  import("@flanksource-ui/pages/views/ViewPage").then((mod) => mod.ViewPage)
);

const ViewsPage = dynamic(
  import("@flanksource-ui/pages/views/ViewsPage").then((mod) => mod.ViewsPage)
);

const ConfigChangesPage = dynamic(
  import("@flanksource-ui/pages/config/ConfigChangesPage").then(
    (mod) => mod.ConfigChangesPage
  )
);

const ConfigAccessPage = dynamic(
  import("@flanksource-ui/pages/config/ConfigAccessPage").then(
    (mod) => mod.ConfigAccessPage
  )
);

const PlaybookRunsPage = dynamic(
  import("@flanksource-ui/pages/playbooks/PlaybookRunsPage").then(
    (mod) => mod.default
  )
);

const PlaybookRunsDetailsPage = dynamic(
  import("@flanksource-ui/pages/playbooks/PlaybookRunsDetails").then(
    (mod) => mod.default
  )
);

const ConfigInsightsPage = dynamic(
  import("@flanksource-ui/pages/config/ConfigInsightsList").then(
    (mod) => mod.ConfigInsightsPage
  )
);

const HealthPage = dynamic(
  import("@flanksource-ui/pages/health").then((mod) => mod.HealthPage)
);

const ConnectionsPage = dynamic(() =>
  import("@flanksource-ui/pages/Settings/ConnectionsPage").then(
    (m) => m.ConnectionsPage
  )
);

const PluginsPage = dynamic(() =>
  import("@flanksource-ui/pages/Settings/PluginsPage").then(
    (m) => m.PluginsPage
  )
);

const EventQueueStatusPage = dynamic(() =>
  import("@flanksource-ui/pages/Settings/EventQueueStatus").then(
    (m) => m.EventQueueStatusPage
  )
);

const FeatureFlagsPage = dynamic(() =>
  import("@flanksource-ui/pages/Settings/FeatureFlagsPage").then(
    (m) => m.FeatureFlagsPage
  )
);

const NotificationSilencedAddPage = dynamic(
  () =>
    import(
      "@flanksource-ui/pages/Settings/notifications/NotificationSilencedAddPage"
    )
);

const NotificationsPage = dynamic(
  () => import("@flanksource-ui/pages/Settings/notifications/NotificationsPage")
);

const NotificationRulesPage = dynamic(
  () =>
    import(
      "@flanksource-ui/pages/Settings/notifications/NotificationsRulesPage"
    )
);

const NotificationsSilencedPage = dynamic(
  () =>
    import(
      "@flanksource-ui/pages/Settings/notifications/NotificationsSilencedPage"
    )
);

const UsersPage = dynamic(() =>
  import("@flanksource-ui/pages/UsersPage").then((m) => m.UsersPage)
);

const ConfigDetailsChecksPage = dynamic(() =>
  import("@flanksource-ui/pages/config/details/ConfigDetailsChecksPage").then(
    (m) => m.ConfigDetailsChecksPage
  )
);

const ConfigDetailsInsightsPage = dynamic(() =>
  import("@flanksource-ui/pages/config/details/ConfigDetailsInsightsPage").then(
    (m) => m.ConfigDetailsInsightsPage
  )
);

const ConfigDetailsPlaybooksPage = dynamic(() =>
  import("@flanksource-ui/pages/config/details/ConfigDetailsPlaybooks").then(
    (m) => m.ConfigDetailsPlaybooksPage
  )
);

const ConfigDetailsRelationshipsPage = dynamic(() =>
  import(
    "@flanksource-ui/pages/config/details/ConfigDetailsRelationshipsPage"
  ).then((mod) => mod.ConfigDetailsRelationshipsPage)
);

const ConfigDetailsAccessPage = dynamic(() =>
  import("@flanksource-ui/pages/config/details/ConfigDetailsAccessPage").then(
    (mod) => mod.ConfigDetailsAccessPage
  )
);

const ConfigDetailsAccessLogsPage = dynamic(() =>
  import(
    "@flanksource-ui/pages/config/details/ConfigDetailsAccessLogsPage"
  ).then((mod) => mod.ConfigDetailsAccessLogsPage)
);

const ConfigDetailsViewPage = dynamic(() =>
  import("@flanksource-ui/pages/config/details/ConfigDetailsViewPage").then(
    (mod) => mod.ConfigDetailsViewPage
  )
);

const ConfigDetailsPluginPage = dynamic(() =>
  import("@flanksource-ui/pages/config/details/ConfigDetailsPluginPage").then(
    (mod) => mod.ConfigDetailsPluginPage
  )
);

const ConfigScrapersEditPage = dynamic(
  () => import("@flanksource-ui/pages/config/settings/ConfigScrapersEditPage")
);

const ConfigScrapersPage = dynamic(
  () => import("@flanksource-ui/pages/config/settings/ConfigScrapersPage")
);

const ConfigPluginsPage = dynamic(
  () => import("@flanksource-ui/pages/config/settings/ConfigPluginsPage")
);

const EditIntegrationPage = dynamic(
  () => import("./components/Integrations/EditIntegrationPage")
);

const IntegrationsPage = dynamic(
  () => import("./components/Integrations/IntegrationsPage")
);

const JobsHistorySettingsPage = dynamic(
  () => import("./components/JobsHistory/JobsHistorySettingsPage")
);

const JobsHistoryDrilldownSettingsPage = dynamic(
  () => import("./components/JobsHistory/JobsHistoryDrilldownSettingsPage")
);

const AgentsPage = dynamic(
  () => import("@flanksource-ui/components/Agents/AgentPage")
);

const TokensPage = dynamic(
  () => import("@flanksource-ui/components/Tokens/TokensPage")
);

const SchemaResourcePage = dynamic(() =>
  import("@flanksource-ui/components/SchemaResourcePage").then(
    (mod) => mod.SchemaResourcePage
  )
);

const ResourceNotificationsPage = dynamic(
  () =>
    import(
      "@flanksource-ui/pages/Settings/notifications/ResourceNotificationsPage"
    )
);

const isDevelopment = process.env.NODE_ENV === "development";

export type NavigationItems = {
  name: string;
  icon: React.ComponentType<any> | IconType;
  href: string;
  featureName: string;
  resourceName: string;
}[];

const navigation: NavigationItems = [
  {
    name: "Health",
    href: "/health",
    icon: AiFillHeart,
    featureName: features.health,
    resourceName: tables.canaries
  },
  {
    name: "Incidents",
    href: "/incidents",
    icon: ImLifebuoy,
    featureName: features.incidents,
    resourceName: tables.incident
  },
  {
    name: "Catalog",
    href: "/catalog",
    icon: VscJson,
    featureName: features.config,
    resourceName: tables.catalog
  },
  {
    name: "Logs",
    href: "/logs",
    icon: LogsIcon,
    featureName: features.logs,
    resourceName: "logs"
  },
  {
    name: "Playbooks",
    href: "/playbooks",
    icon: ({ className }: { className?: string }) => (
      <Icon name="playbook" className={`${className} text-white`} />
    ),
    featureName: features.playbooks,
    resourceName: tables.playbooks
  },
  {
    name: "Applications",
    href: "/applications",
    icon: ({ className }: { className?: string }) => (
      <Icon name="workflow" className={`${className} text-white`} />
    ),
    featureName: features.applications,
    resourceName: tables.applications
  }
];

export type SettingsNavigationItems = {
  name: string;
  icon: IconType;
  checkPath: boolean;
  submenu: (
    | (SchemaResourceType & { href: string })
    | {
        name: string;
        href: string;
        icon: React.ComponentType<{ className: string }>;
        featureName: string;
        resourceName: string | string[];
      }
  )[];
};

const settingsNav: SettingsNavigationItems = {
  name: "Settings",
  icon: AdjustmentsIcon,
  checkPath: false,
  submenu: [
    {
      name: "Artifacts",
      href: "/settings/artifacts",
      icon: FaArchive,
      featureName: features["settings.artifacts"],
      resourceName: tables.artifacts
    },
    {
      name: "Connections",
      href: "/settings/connections",
      icon: BsLink,
      featureName: features["settings.connections"],
      resourceName: tables.connections
    },
    {
      name: "Permissions",
      href: "/settings/permissions",
      icon: RiShieldUserFill,
      featureName: features["settings.permissions"],
      resourceName: PERMISSIONS_TAB_RESOURCES.map(({ resource }) => resource)
    },
    ...(process.env.NEXT_PUBLIC_AUTH_IS_CLERK === "true"
      ? []
      : [
          {
            name: "Users",
            href: "/settings/users",
            icon: HiUser,
            featureName: features["settings.users"],
            resourceName: tables.people
          }
        ]),
    ...schemaResourceTypes.map((x) => ({
      ...x,
      href:
        x.table === "config_scrapers"
          ? "/catalog/scrapers"
          : `/settings/${x.table}`
    })),
    {
      name: "Jobs History",
      href: "/settings/jobs",
      icon: FaTasks,
      featureName: features["settings.job_history"],
      resourceName: tables.jobs
    },
    {
      name: "MCP",
      href: "/settings/mcp",
      icon: ({ className }: { className: string }) => (
        <Icon name="mcp" className={`${className} [&_path]:!fill-white`} />
      ),
      featureName: features["settings.mcp"],
      resourceName: MCP_TAB_RESOURCES.map(({ resource }) => resource)
    },
    {
      name: "Properties",
      href: "/settings/properties",
      icon: BsToggles,
      featureName: features["settings.feature_flags"],
      resourceName: tables.properties
    },
    {
      name: "Log Backends",
      href: "/settings/log-backends",
      icon: LogsIcon,
      featureName: features["logs"],
      resourceName: tables.logging_backends
    },
    {
      name: "Event Queue",
      href: "/settings/event-queue-status",
      icon: FaTasks,
      featureName: features["settings.event_queue_status"],
      resourceName: tables.events
    },
    {
      name: "Agents",
      href: "/settings/agents",
      icon: MdOutlineSupportAgent,
      featureName: features.agents,
      resourceName: tables.agents
    },
    {
      name: "Personal access tokens",
      href: "/settings/tokens",
      icon: VscKey,
      featureName: features.agents,
      resourceName: tables.tokens
    },
    {
      name: "Notifications",
      href: "/notifications/rules",
      icon: FaBell,
      featureName: features["settings.notifications"],
      resourceName: [tables.notifications, tables.notification_silences]
    },
    {
      name: "Integrations",
      href: "/settings/integrations",
      icon: MdOutlineIntegrationInstructions,
      featureName: features["settings.integrations"],
      resourceName: tables.integrations
    },
    {
      name: "Plugins",
      href: "/settings/plugins",
      icon: FaPlug,
      featureName: features["settings.plugins"],
      resourceName: tables.plugins
    },
    {
      name: "Views",
      href: "/settings/views",
      icon: ({ className }: { className: string }) => (
        <Icon name="workflow" className={className} />
      ),
      featureName: features.views,
      resourceName: tables.views
    }
  ].sort((v1, v2) => stringSortHelper(v1.name, v2.name))
};

const GLOBAL_PAGE_ACCESS_RESOURCES = [
  ...new Set([
    ...navigation.map((item) => item.resourceName),
    ...settingsNav.submenu.flatMap((item) => item.resourceName),
    tables.views
  ])
];

function useHasAnyPageAccess() {
  const { hasAnyResourceAccess } = useUserAccessStateContext();
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [hasAnyPageAccess, setHasAnyPageAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsCheckingAccess(true);

    hasAnyResourceAccess(GLOBAL_PAGE_ACCESS_RESOURCES, "read").then(
      (hasAccess) => {
        if (cancelled) {
          return;
        }

        setHasAnyPageAccess(hasAccess);
        setIsCheckingAccess(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [hasAnyResourceAccess]);

  return { hasAnyPageAccess, isCheckingAccess };
}

const CANARY_API = "/api/canary/api/summary";

export function HealthRoutes({ sidebar }: { sidebar: ReactNode }) {
  return (
    <Routes>
      <Route
        index
        element={withAuthorizationAccessCheck(
          <HealthPage url={CANARY_API} />,
          tables.canaries,
          "read"
        )}
      />
    </Routes>
  );
}

export function IncidentManagerRoutes({ sidebar }: { sidebar: ReactNode }) {
  const { featureFlagsLoaded } = useFeatureFlagsContext();
  const { hasAnyPageAccess, isCheckingAccess } = useHasAnyPageAccess();

  if (
    !featureFlagsLoaded &&
    !window.location.pathname.startsWith("/view/topology")
  ) {
    return <FullPageSkeletonLoader />;
  }

  if (isCheckingAccess) {
    return <FullPageSkeletonLoader />;
  }

  if (!hasAnyPageAccess) {
    return <NoPermissionsOnboardingPage />;
  }

  return (
    <Routes>
      <Route path="" element={sidebar}>
        <Route index element={<HomepageRedirect />} />
      </Route>

      <Route
        path="/view/topology/:id"
        element={withAuthorizationAccessCheck(
          <TopologyCardPage />,
          tables.topologies,
          "read",
          true
        )}
      />

      <Route path="/view/health" element={<HealthPage url={CANARY_API} />} />

      <Route path="topology" element={sidebar}>
        <Route
          index
          element={withAuthorizationAccessCheck(
            <TopologyPage />,
            tables.topologies,
            "read",
            true
          )}
        />

        <Route
          path=":id"
          element={withAuthorizationAccessCheck(
            <TopologyPage />,
            tables.topologies,
            "read",
            true
          )}
        />
      </Route>

      <Route path="incidents" element={sidebar}>
        <Route
          path=":id"
          element={
            <ErrorBoundary>
              {withAuthorizationAccessCheck(
                <IncidentDetailsPage />,
                tables.incident,
                "read",
                true
              )}
            </ErrorBoundary>
          }
        />
        <Route index element={<IncidentListPage />} />
      </Route>

      <Route path="health" element={sidebar}>
        <Route
          index
          element={withAuthorizationAccessCheck(
            <HealthPage url={CANARY_API} />,
            tables.canaries,
            "read"
          )}
        />
      </Route>

      <Route path="playbooks" element={sidebar}>
        <Route
          index
          element={withAuthorizationAccessCheck(
            <PlaybooksListPage />,
            tables.playbooks,
            "read",
            true
          )}
        />

        <Route path="runs">
          <Route
            index
            element={withAuthorizationAccessCheck(
              <PlaybookRunsPage />,
              tables.playbooks,
              "read"
            )}
          />

          <Route
            path=":id"
            element={withAuthorizationAccessCheck(
              <PlaybookRunsDetailsPage />,
              tables.playbooks,
              "read",
              true
            )}
          />
        </Route>
      </Route>

      <Route path="applications" element={sidebar}>
        <Route
          index
          element={withAuthorizationAccessCheck(
            <ApplicationsPage />,
            tables.applications,
            "read",
            true
          )}
        />
        <Route
          path=":namespace/:name"
          element={withAuthorizationAccessCheck(
            <AuditReportPage />,
            tables.applications,
            "read",
            true
          )}
        />
      </Route>

      <Route path="getting-started" element={sidebar}>
        <Route index element={<GettingStartedPage />} />
      </Route>

      <Route path="views" element={sidebar}>
        <Route
          path=":id"
          element={withAuthorizationAccessCheck(
            <ViewPage />,
            tables.views,
            "read",
            true
          )}
        />
      </Route>

      <Route path="view" element={sidebar}>
        <Route
          path=":namespace/:name"
          element={withAuthorizationAccessCheck(
            <ViewPage />,
            tables.views,
            "read",
            true
          )}
        />
        <Route
          path=":name"
          element={withAuthorizationAccessCheck(
            <ViewPage />,
            tables.views,
            "read",
            true
          )}
        />
      </Route>

      <Route path="notifications" element={sidebar}>
        <Route
          index
          element={withAuthorizationAccessCheck(
            <NotificationsPage />,
            tables.notifications,
            "read",
            true
          )}
        />

        <Route
          path="rules"
          element={withAuthorizationAccessCheck(
            <NotificationRulesPage />,
            tables.notifications,
            "read",
            true
          )}
        />

        <Route
          path="resource/:resourceId"
          element={withAuthorizationAccessCheck(
            <ResourceNotificationsPage />,
            tables.notifications,
            "read",
            true
          )}
        />

        <Route path="silences">
          <Route
            index
            element={withAuthorizationAccessCheck(
              <NotificationsSilencedPage />,
              tables.notification_silences,
              "read",
              true
            )}
          />

          <Route
            path="add"
            element={withAuthorizationAccessCheck(
              <NotificationSilencedAddPage />,
              tables.notification_silences,
              "create",
              true
            )}
          />
        </Route>
      </Route>

      <Route path="settings" element={sidebar}>
        <Route
          path="artifacts"
          element={withAuthorizationAccessCheck(
            <ArtifactsPage />,
            tables.artifacts,
            "read",
            true
          )}
        />
        <Route
          path="connections"
          element={withAuthorizationAccessCheck(
            <ConnectionsPage />,
            tables.connections,
            "read",
            true
          )}
        />
        <Route path="permissions" element={<PermissionsIndexRedirect />} />
        <Route
          path="permissions/subjects"
          element={withAuthorizationAccessCheck(
            <PermissionsSubjectsPage />,
            tables.access_review,
            "read",
            true
          )}
        />
        <Route
          path="permissions/scopes"
          element={withAuthorizationAccessCheck(
            <ScopesPage />,
            tables.scopes,
            "read",
            true
          )}
        />
        <Route
          path="scopes"
          element={<Navigate to="/settings/permissions/scopes" replace />}
        />
        <Route
          path="permissions/roles"
          element={withAuthorizationAccessCheck(
            <RolesPage />,
            tables.roles,
            "read",
            true
          )}
        />
        <Route
          path="permissions/legacy-permissions"
          element={withAuthorizationAccessCheck(
            <PermissionsPage />,
            tables.role_bindings,
            "read",
            true
          )}
        />
        <Route
          path="permissions/legacy-permission-groups"
          element={withAuthorizationAccessCheck(
            <PermissionGroupsPage />,
            tables.role_bindings,
            "read",
            true
          )}
        />
        <Route
          path="permissions/role-bindings"
          element={withAuthorizationAccessCheck(
            <RoleBindingsPage />,
            tables.role_bindings,
            "read",
            true
          )}
        />
        <Route
          path="users"
          element={withAuthorizationAccessCheck(
            <UsersPage />,
            tables.people,
            "read",
            true
          )}
        />
        <Route
          path="jobs"
          element={withAuthorizationAccessCheck(
            <JobsHistorySettingsPage />,
            tables.jobs,
            "read",
            true
          )}
        />
        <Route
          path="jobs/:jobName"
          element={withAuthorizationAccessCheck(
            <JobsHistoryDrilldownSettingsPage />,
            tables.jobs,
            "read",
            true
          )}
        />

        <Route
          path="properties"
          element={withAuthorizationAccessCheck(
            <FeatureFlagsPage />,
            tables.properties,
            "read",
            true
          )}
        />

        <Route
          path="event-queue-status"
          element={withAuthorizationAccessCheck(
            <EventQueueStatusPage />,
            tables.events,
            "read",
            true
          )}
        />

        <Route path="agents">
          <Route
            index
            element={withAuthorizationAccessCheck(
              <AgentsPage />,
              tables.agents,
              "read",
              true
            )}
          />
        </Route>

        <Route path="tokens">
          <Route
            index
            element={withAuthorizationAccessCheck(
              <TokensPage />,
              tables.tokens,
              "read",
              true
            )}
          />
        </Route>

        <Route path="integrations">
          <Route
            index
            element={withAuthorizationAccessCheck(
              <IntegrationsPage />,
              tables.integrations,
              "read",
              true
            )}
          />

          <Route path=":type" caseSensitive>
            <Route
              path=":id"
              element={withAuthorizationAccessCheck(
                <EditIntegrationPage />,
                tables.integrations,
                "write",
                true
              )}
            />
          </Route>
        </Route>

        <Route
          path="plugins"
          element={withAuthorizationAccessCheck(
            <PluginsPage />,
            tables.plugins,
            "read",
            true
          )}
        />

        <Route path="views">
          <Route
            index
            element={withAuthorizationAccessCheck(
              <ViewsPage />,
              tables.views,
              "read",
              true
            )}
          />
        </Route>

        <Route path="mcp">
          <Route index element={<McpIndexRedirect />} />
          <Route
            path="overview"
            element={withAuthorizationAccessCheck(
              <McpOverviewPage />,
              tables.role_bindings,
              "read",
              true
            )}
          />
          <Route
            path="playbooks"
            element={withAuthorizationAccessCheck(
              <McpPlaybooksPage />,
              tables.role_bindings,
              "read",
              true
            )}
          />
          <Route
            path="views"
            element={withAuthorizationAccessCheck(
              <McpViewsPage />,
              tables.role_bindings,
              "read",
              true
            )}
          />
          <Route
            path="subject-access"
            element={withAuthorizationAccessCheck(
              <McpSubjectAccessPage />,
              tables.access_review,
              "read",
              true
            )}
          />
          <Route
            path="check-access"
            element={withAuthorizationAccessCheck(
              <McpCheckAccessPage />,
              tables.access_review,
              "read",
              true
            )}
          />
        </Route>

        {settingsNav.submenu
          .filter((v) => (v as SchemaResourceType).table)
          .map((x) => {
            return (
              <Route key={x.name} path={(x as SchemaResourceType).table}>
                <Route
                  index
                  key={`${x.name}-list`}
                  element={withAuthorizationAccessCheck(
                    <SchemaResourcePage
                      resourceInfo={x as SchemaResourceType & { href: string }}
                    />,
                    x.resourceName,
                    "read",
                    true
                  )}
                />
                <Route
                  key={`${x.name}-detail`}
                  path={`:id`}
                  element={withAuthorizationAccessCheck(
                    <SchemaResource resourceInfo={x as SchemaResourceType} />,
                    x.resourceName,
                    "read",
                    true
                  )}
                />
              </Route>
            );
          })}
      </Route>

      <Route path="logs" element={sidebar}>
        <Route
          index
          element={
            <ErrorBoundary>
              {withAuthorizationAccessCheck(<LogsPage />, "logs", "read", true)}
            </ErrorBoundary>
          }
        />
      </Route>

      {/* Redirect configs to catalog */}
      <Route path="configs" element={<Navigate to="/catalog" />} />

      <Route path="catalog" element={sidebar}>
        <Route
          index
          element={withAuthorizationAccessCheck(
            <ConfigListPage />,
            tables.catalog,
            "read",
            true
          )}
        />
        <Route
          path="changes"
          element={withAuthorizationAccessCheck(
            <ConfigChangesPage />,
            tables.catalog,
            "read",
            true
          )}
        />
        <Route
          path="access"
          element={withAuthorizationAccessCheck(
            <ConfigAccessPage />,
            tables.catalog,
            "read",
            true
          )}
        />
        <Route
          path="insights"
          element={withAuthorizationAccessCheck(
            <ConfigInsightsPage />,
            tables.catalog,
            "read",
            true
          )}
        />
        <Route path="scrapers">
          <Route
            index
            element={withAuthorizationAccessCheck(
              <ConfigScrapersPage />,
              tables.catalog,
              "read",
              true
            )}
          />

          <Route
            path=":id"
            element={withAuthorizationAccessCheck(
              <ConfigScrapersEditPage />,
              tables.catalog,
              "read",
              true
            )}
          />
        </Route>
        <Route
          path="plugins"
          element={withAuthorizationAccessCheck(
            <ConfigPluginsPage />,
            tables.catalog,
            "read",
            true
          )}
        />
        <Route path=":id">
          <Route
            index
            element={
              <ErrorBoundary>
                {withAuthorizationAccessCheck(
                  <ConfigDetailsIndexRedirect />,
                  tables.catalog,
                  "read",
                  true
                )}
              </ErrorBoundary>
            }
          />
          <Route
            path="spec"
            element={
              <ErrorBoundary>
                {withAuthorizationAccessCheck(
                  <ConfigDetailsPage />,
                  tables.catalog,
                  "read",
                  true
                )}
              </ErrorBoundary>
            }
          />
          <Route
            path="changes"
            element={withAuthorizationAccessCheck(
              <ConfigDetailsChangesPage />,
              tables.catalog,
              "read",
              true
            )}
          />
          <Route
            path="insights"
            element={withAuthorizationAccessCheck(
              <ConfigDetailsInsightsPage />,
              tables.catalog,
              "read",
              true
            )}
          />
          <Route
            path="relationships"
            element={withAuthorizationAccessCheck(
              <ConfigDetailsRelationshipsPage />,
              tables.catalog,
              "read",
              true
            )}
          />
          <Route
            path="playbooks"
            element={withAuthorizationAccessCheck(
              <ConfigDetailsPlaybooksPage />,
              tables.catalog,
              "read",
              true
            )}
          />
          <Route
            path="checks"
            element={withAuthorizationAccessCheck(
              <ConfigDetailsChecksPage />,
              tables.catalog,
              "read"
            )}
          />
          <Route
            path="access"
            element={withAuthorizationAccessCheck(
              <ConfigDetailsAccessPage />,
              tables.catalog,
              "read"
            )}
          />
          <Route
            path="access-logs"
            element={withAuthorizationAccessCheck(
              <ConfigDetailsAccessLogsPage />,
              tables.catalog,
              "read"
            )}
          />
          <Route
            path="view/:viewId"
            element={withAuthorizationAccessCheck(
              <ConfigDetailsViewPage />,
              tables.catalog,
              "read",
              true
            )}
          />
          <Route
            path="plugin/:pluginName/:tabName"
            element={withAuthorizationAccessCheck(
              <ConfigDetailsPluginPage />,
              tables.catalog,
              "read",
              true
            )}
          />
        </Route>
      </Route>
    </Routes>
  );
}

export function CanaryCheckerApp() {
  const [isBrowserEnv, setIsBrowserEnv] = useState(false);

  useEffect(() => {
    setIsBrowserEnv(true);
  }, []);

  if (!isBrowserEnv) {
    return null;
  }

  // TODO(ciju): the url is set at two places. axios.js#CanaryChecker and here.
  // Consolidate logic to one place.
  if (typeof window === "undefined") {
    return <FullPageSkeletonLoader />;
  }

  return (
    <>
      <Head prefix={"Canary Checker"} suffix="" />
      <BrowserRouter>
        <ToasterWithCloseButton />
        <Provider>
          <HealthPageContextProvider>
            <Canary url="/api/canary/api/summary" />
            <ReactQueryDevtools initialIsOpen={false} />
          </HealthPageContextProvider>
        </Provider>
      </BrowserRouter>
    </>
  );
}

function findDashboardView(
  views: ViewSummary[],
  dashboardViewValue?: string
): ViewSummary | undefined {
  if (!dashboardViewValue) {
    return views.find((v) => v.name === FALLBACK_VIEW_NAME);
  }

  if (UUID_REGEX.test(dashboardViewValue)) {
    return views.find((v) => v.id === dashboardViewValue);
  }

  if (dashboardViewValue.includes("/")) {
    const [namespace, name] = dashboardViewValue.split("/", 2);
    return views.find((v) => v.namespace === namespace && v.name === name);
  }

  return views.find((v) => v.name === dashboardViewValue);
}

function useDynamicNavigation() {
  const { featureFlags } = useFeatureFlagsContext();
  const { access } = useUser();
  // whoami tells us up front whether the user can read views at all.
  const viewRead = access?.[tables.views]?.read;
  const canReadViews = viewRead === "all" || viewRead === "some";
  const { data: views = [] } = useQuery<ViewSummary[]>({
    queryKey: ["views-sidebar"],
    queryFn: getViewsForSidebar,
    enabled: canReadViews,
    staleTime: 5 * 60 * 1000
  });

  const dashboardViewValue = featureFlags.find(
    (f) => f.name === DASHBOARD_VIEW_PROPERTY
  )?.value;

  return useMemo(() => {
    const dashboardView = findDashboardView(views, dashboardViewValue);

    const viewsNavigationItems = views
      .filter((view) => view.id !== dashboardView?.id)
      .map((view) => ({
        name: view.title || view.name,
        href: `/views/${view.id}`,
        icon: ({ className }: { className?: string }) => (
          <Icon
            name={view.icon || "workflow"}
            className={`${className} text-white`}
          />
        ),
        featureName: features.views,
        resourceName: tables.views
      }));

    const dashboardNavItem = dashboardView
      ? [
          {
            name: dashboardView.title || dashboardView.name,
            href: "/",
            icon: ({ className }: { className?: string }) => (
              <Icon
                name={dashboardView.icon || "workflow"}
                className={`${className} text-white`}
              />
            ),
            featureName: features.views,
            resourceName: tables.views
          }
        ]
      : [];

    return [...dashboardNavItem, ...navigation, ...viewsNavigationItems];
  }, [views, dashboardViewValue]);
}

function SidebarWrapper() {
  const location = useLocation();
  const url = location.pathname.split("/");
  const path = url[2];
  const dynamicNavigation = useDynamicNavigation();

  const pathTrack = [
    "users",
    "jobs",
    "teams",
    "incident_rules",
    "config_scrapers",
    "topologies",
    "canaries",
    "connections",
    "properties",
    "jobs"
  ];
  const checkPath = pathTrack.includes(path);

  return (
    <SidebarLayout
      navigation={dynamicNavigation}
      settingsNav={settingsNav}
      checkPath={checkPath}
    />
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Provider>
        <ToasterWithCloseButton />
        <AuthProviderWrapper>
          <UserAccessStateContextProvider>
            <HealthPageContextProvider>
              <ConfigPageContextProvider>
                <IncidentPageContextProvider>
                  <IncidentManagerRoutes sidebar={<SidebarWrapper />} />
                  {isDevelopment && (
                    <ReactQueryDevtools initialIsOpen={false} />
                  )}
                </IncidentPageContextProvider>
              </ConfigPageContextProvider>
            </HealthPageContextProvider>
          </UserAccessStateContextProvider>
        </AuthProviderWrapper>
      </Provider>
    </BrowserRouter>
  );
}

// The Permissions sidebar entry opens the first of its tabs the user may read.
function PermissionsIndexRedirect() {
  const { access, access_actions } = useUser();
  const tab = PERMISSIONS_TAB_RESOURCES.find((tab) =>
    canOpenPermissionsTab(tab, access, access_actions)
  );
  return (
    <Navigate to={tab?.path ?? "/settings/permissions/role-bindings"} replace />
  );
}

// The MCP sidebar entry opens the first of its tabs the user may open.
function McpIndexRedirect() {
  const { access, access_actions } = useUser();
  const tab = MCP_TAB_RESOURCES.find((tab) =>
    canOpenPermissionsTab(tab, access, access_actions)
  );
  return <Navigate to={tab?.path ?? "/settings/mcp/overview"} replace />;
}
